import React from "react";

export type RequestStatus = "idle" | "success" | "error";

export interface CsvImportExecutionRow {
    isValid: boolean;
    invalidReason?: string;
    sortDate?: string;
    requestStatus: RequestStatus;
    requestMessage?: string;
}

export interface CsvImportStatusTexts {
    invalidRow: string;
    validRow: string;
    notExecutedBecauseValidationFailed: string;
    uploadedSuccessfully: string;
    uploadFailed: string;
    notExecutedYet: string;
}

export interface CsvImportMessageTexts {
    noValidRows: string;
    uploadedSuccessfully: string;
    requestFailed: string;
    formatStartUpload: (count: number, itemLabel: string) => string;
    formatFinishedUpload: (itemLabel: string, successCount: number, failedCount: number) => string;
}

export const defaultCsvImportStatusTexts: CsvImportStatusTexts = {
    invalidRow: "Invalid row",
    validRow: "Valid row",
    notExecutedBecauseValidationFailed: "Not executed because validation failed.",
    uploadedSuccessfully: "Uploaded successfully.",
    uploadFailed: "Upload failed.",
    notExecutedYet: "Not executed yet.",
};

const defaultCsvImportMessageTexts: CsvImportMessageTexts = {
    noValidRows: "No valid rows available for upload.",
    uploadedSuccessfully: "Uploaded successfully.",
    requestFailed: "Request failed.",
    formatStartUpload: (count, itemLabel) => `Starting upload for ${count} ${itemLabel} row(s)...`,
    formatFinishedUpload: (itemLabel, successCount, failedCount) => `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} upload finished: ${successCount} succeeded, ${failedCount} failed.`,
};

export const toLocalDate = (dateOnly: string) => {
    const [year, month, day] = dateOnly.split("-").map(Number);
    return new Date(year, month - 1, day);
};

export const getRequestErrorMessage = (err: unknown, fallbackMessage = "Request failed.") => {
    console.log("getRequestErrorMessage", err);
    if (typeof err === "string") return err;
    if (err && typeof err === "object" && "message" in err && typeof (err as { message?: unknown }).message === "string") {
        return (err as { message: string }).message;
    }

    return fallbackMessage;
};

export const getValidationStatusTitle = <TRow extends CsvImportExecutionRow>(
    row: TRow,
    texts: CsvImportStatusTexts = defaultCsvImportStatusTexts,
) => {
    if (!row.isValid) return row.invalidReason ?? texts.invalidRow;
    return texts.validRow;
};

export const getRequestStatusTitle = <TRow extends CsvImportExecutionRow>(
    row: TRow,
    texts: CsvImportStatusTexts = defaultCsvImportStatusTexts,
) => {
    if (!row.isValid) return texts.notExecutedBecauseValidationFailed;
    if (row.requestStatus === "success") return row.requestMessage ?? texts.uploadedSuccessfully;
    if (row.requestStatus === "error") return row.requestMessage ?? texts.uploadFailed;
    return texts.notExecutedYet;
};

export const sortImportedRowsByDate = <TRow extends { sortDate?: string; date: string }>(rows: TRow[]) => {
    return [...rows].sort((a, b) => {
        if (a.sortDate && b.sortDate) return a.sortDate.localeCompare(b.sortDate);
        if (a.sortDate) return -1;
        if (b.sortDate) return 1;
        return a.date.localeCompare(b.date);
    });
};

export const getValidRowCount = <TRow extends { isValid: boolean }>(rows: TRow[]) => {
    return rows.filter((row) => row.isValid).length;
};

const MAX_PARALLEL_UPLOAD_REQUESTS = 20;

export const useCsvImportState = <TRow extends CsvImportExecutionRow>() => {
    const [rows, setRows] = React.useState<TRow[]>([]);
    const [fileName, setFileName] = React.useState<string>();
    const [message, setMessage] = React.useState<string>();
    const [error, setError] = React.useState<string>();
    const [isBatchRunning, setIsBatchRunning] = React.useState(false);
    const [hasBatchExecuted, setHasBatchExecuted] = React.useState(false);
    const batchExecutionLock = React.useRef(false);

    const validRowCount = React.useMemo(() => getValidRowCount(rows), [rows]);

    const resetForNewFile = React.useCallback((nextFileName: string) => {
        setFileName(nextFileName);
        setError(undefined);
        setMessage(undefined);
        setHasBatchExecuted(false);
        batchExecutionLock.current = false;
    }, []);

    const executeBatch = React.useCallback(async (
        itemLabel: string,
        uploadRow: (row: TRow & { sortDate: string }) => Promise<void>,
        messageTexts: CsvImportMessageTexts = defaultCsvImportMessageTexts,
    ) => {
        if (isBatchRunning || hasBatchExecuted || batchExecutionLock.current) return;

        batchExecutionLock.current = true;
        setHasBatchExecuted(true);

        if (validRowCount === 0) {
            setError(messageTexts.noValidRows);
            setHasBatchExecuted(false);
            batchExecutionLock.current = false;
            return;
        }

        setIsBatchRunning(true);
        setError(undefined);
        setMessage(messageTexts.formatStartUpload(validRowCount, itemLabel));
        setRows((prevRows) => prevRows.map((row) => ({
            ...row,
            requestStatus: row.isValid ? "idle" : row.requestStatus,
            requestMessage: row.isValid ? undefined : row.requestMessage,
        })));

        const validRows = rows
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => row.isValid && !!row.sortDate)
            .map(({ row, index }) => ({ row: row as TRow & { sortDate: string }, index }));

        const requestResults: Array<{ index: number; requestStatus: RequestStatus; requestMessage: string }> = [];

        for (let chunkStart = 0; chunkStart < validRows.length; chunkStart += MAX_PARALLEL_UPLOAD_REQUESTS) {
            const chunk = validRows.slice(chunkStart, chunkStart + MAX_PARALLEL_UPLOAD_REQUESTS);
            const chunkResults = await Promise.allSettled(chunk.map(async ({ row, index }) => {
                await uploadRow(row);
                return {
                    index,
                    requestStatus: "success" as RequestStatus,
                    requestMessage: messageTexts.uploadedSuccessfully,
                };
            }));

            chunkResults.forEach((result, chunkIndex) => {
                const index = chunk[chunkIndex].index;

                if (result.status === "fulfilled") {
                    requestResults.push(result.value);
                    return;
                }

                requestResults.push({
                    index,
                    requestStatus: "error",
                    requestMessage: getRequestErrorMessage(result.reason, messageTexts.requestFailed),
                });
            });
        }

        const requestResultsByIndex = new Map<number, { requestStatus: RequestStatus; requestMessage: string }>(
            requestResults.map((result) => [result.index, {
                requestStatus: result.requestStatus,
                requestMessage: result.requestMessage,
            }])
        );

        const successCount = requestResults.filter((result) => result.requestStatus === "success").length;
        const failedCount = requestResults.length - successCount;

        setRows((prevRows) => prevRows.map((currentRow, currentIndex) => {
            const requestResult = requestResultsByIndex.get(currentIndex);
            if (!requestResult) return currentRow;

            return {
                ...currentRow,
                requestStatus: requestResult.requestStatus,
                requestMessage: requestResult.requestMessage,
            };
        }));

        setMessage(messageTexts.formatFinishedUpload(itemLabel, successCount, failedCount));
        setIsBatchRunning(false);
    }, [hasBatchExecuted, isBatchRunning, rows, validRowCount]);

    return {
        rows,
        setRows,
        fileName,
        message,
        error,
        setMessage,
        setError,
        isBatchRunning,
        hasBatchExecuted,
        validRowCount,
        resetForNewFile,
        executeBatch,
    };
};